import React, { useEffect, useMemo, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { I18n, Translate } from 'react-redux-i18n'
import authProvider from '../../../core/AuthenticationProvider'
import { Element } from '../../common/html/element'
import { Icon } from '../../common/html/icon'
import { Loading } from '../../common/loading'
import { ScrollComponent } from '../../common/scrollComponent'
import { TextEllipsisStaticLine } from '../../common/textEllipsis/TextEllipsisStaticLine'
import { debounce } from '../../helpers/common'
import { STATUS_MESS } from '../constants'
import {
  changeShowHistoryList,
  changeTopicActive,
  changStatusMess,
  createNewTopic,
  selectHistoryList,
  selectTopicActive,
  selectTopicMaxUpdatedDate,
  showHistoryList,
} from '../store/slice'
import {
  getListTopic,
  getListTopicScroll,
  getMessageInTopic,
  updateTopic,
} from '../store/thunk'
import { PopupDeleteHistoryChatbot } from './PopupDeleteHistoryChatbot'

const HistoryChatbot = () => {
  const dispatch = useDispatch()
  const isShowHistoryList = useSelector(showHistoryList)
  const historyList = useSelector(selectHistoryList)
  const topicActive = useSelector(selectTopicActive)
  const topicMaxUpdatedDate = useSelector(selectTopicMaxUpdatedDate)
  const loading = false //useSelector(loadingHistoryList)

  const [editingTopicId, setEditingTopicId] = React.useState(null)
  const [editingTitle, setEditingTitle] = React.useState('')
  const [isShowPopupDelete, setShowPopupDelete] = useState(false)
  const [topicSelectDelete, setTopicSelectDelete] = useState({})
  const [searchHistoryText, setSearchHistoryText] = useState('')
  const newChatText = I18n.t('chatbot.NEW_CHAT')

  const toggleSessionHistory = () => {
    dispatch(changeShowHistoryList(!isShowHistoryList))
    setSearchHistoryText('')
  }

  const handleOverlayClick = () => {
    if (editingTopicId) return
    toggleSessionHistory()
  }

  const handleSelectSession = (topic) => {
    if (editingTopicId) return
    dispatch(changeTopicActive(topic))
    authProvider.getUser().then((user) => {
      dispatch(
        getMessageInTopic({
          // UserId: user?.profile?.user_id,
          ProductId: 1, // 1: FiinGate,
          TopicId: topic?.topicId,
          PageSize: 20,
        }),
      )
    })
    dispatch(changeShowHistoryList(false))
    dispatch(changStatusMess(STATUS_MESS.ACTIVE))
    setSearchHistoryText('')
  }

  const handleCreateNewTopic = () => {
    if (topicActive?.topicId) {
      dispatch(createNewTopic())
      dispatch(changeShowHistoryList(false))
      setSearchHistoryText('')
    }
    return
  }

  const handleDeleteTopic = (topic) => {
    setShowPopupDelete(true)
    setTopicSelectDelete(topic)
  }

  const startEditing = (e, topic) => {
    e.stopPropagation()
    setEditingTopicId(topic?.topicId)
    setEditingTitle(topic?.topicTitle || '')
  }

  const editInputRef = useRef(null)

  const cancelEditing = () => {
    setEditingTopicId(null)
    setEditingTitle('')
  }

  const commitEditing = async () => {
    const title = editingTitle?.trim()
    if (!editingTopicId) return cancelEditing()
    if (!title) return cancelEditing()
    try {
      // const user = await authProvider.getUser()
      dispatch(
        updateTopic({
          // UserId: user?.profile?.user_id,
          // ProductId: 1, // 1: FiinGate
          TopicId: editingTopicId,
          TopicTitle: title,
        }),
      )
    } finally {
      cancelEditing()
    }
  }

  const onInputKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault()
      commitEditing()
    } else if (e.key === 'Escape') {
      e.preventDefault()
      cancelEditing()
    }
  }

  const handleScrollBottomList = () => {
    authProvider.getUser().then((user) => {
      const lastTopic = historyList[historyList.length - 1]
      const lastTopicId = historyList[historyList.length - 1]?.topicId
      if (lastTopicId) {
        dispatch(
          getListTopicScroll({
            UserId: user?.profile?.user_id,
            ProductId: 1, // 1: FiinGate
            PageSize: 20,
            TopicTitle: searchHistoryText,
            LastTopicId: lastTopic?.topicId,
            InitialMaxUpdate: topicMaxUpdatedDate,
            ScrollId: lastTopic?.scrollId,
          }),
        )
      }
    })
  }

  const handleSearchChange = (e) => {
    setSearchHistoryText(e.target.value)
  }

  const debouncedFetch = useMemo(
    () =>
      debounce((text) => {
        authProvider.getUser().then((user) => {
          dispatch(
            getListTopic({
              UserId: user?.profile?.user_id,
              ProductId: 1, // 1: FiinGate
              PageSize: 20,
              TopicTitle: text,
              InitialMaxUpdate: topicMaxUpdatedDate,
            }),
          )
        })
      }, 1000),
    [dispatch, topicMaxUpdatedDate],
  )

  useEffect(() => {
    if (!isShowHistoryList) return
    debouncedFetch(searchHistoryText)
  }, [
    searchHistoryText,
    debouncedFetch,
    topicActive?.topicId,
    isShowHistoryList,
  ])

  useEffect(() => {
    if (!editingTopicId) return

    const handleClickOutside = (e) => {
      // nếu đang edit thì check
      if (
        editInputRef.current &&
        !editInputRef.current.contains(e.target) &&
        !e.target.closest('.icon-delete') &&
        !e.target.closest('.icon-tick')
      ) {
        cancelEditing()
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [editingTopicId])

  if (!isShowHistoryList) return <></>

  return (
    <>
      <div className="session-overlay" onClick={handleOverlayClick} />
      <div className="session-history-panel">
        <div className="session-header">
          <Element className="session-text-header" style={{ fontSize: 22 }}>
            <Translate value="chatbot.SESSION_HISTORY" />
          </Element>
          <Icon
            className="icon-double-arrow-left collapse-btn"
            onClick={toggleSessionHistory}
          />
        </div>
        <div className="search-container">
          <div className="search-input">
            <Icon className="icon-search search-icon" />
            <Element
              elementName="input"
              type="text"
              placeholder={I18n.t('chatbot.SEARCH')}
              style={{
                fontSize: 14,
              }}
              onChange={handleSearchChange}
            />
          </div>
        </div>

        {loading ? (
          <div style={{ height: '100%' }}>
            <Loading />
          </div>
        ) : (
          <ScrollComponent scrollToBottomCallback={handleScrollBottomList}>
            <div className="session-list">
              {historyList.map((topic, index) => {
                const isActive = topicActive?.topicId === topic.topicId
                const isEditing = editingTopicId === topic.topicId
                return (
                  <Element
                    style={{ fontSize: 14 }}
                    key={index}
                    className={`session-item ${isActive ? 'active' : ''} ${
                      isEditing ? 'editing' : ''
                    }`}
                    onClick={() => handleSelectSession(topic)}
                  >
                    {isEditing ? (
                      <input
                        ref={editInputRef}
                        className="session-edit-input"
                        autoFocus
                        value={editingTitle}
                        onChange={(e) => setEditingTitle(e.target.value)}
                        onKeyDown={onInputKeyDown}
                        onClick={(e) => e.stopPropagation()}
                        maxLength={200}
                      />
                    ) : (
                      <span className="session-name">
                        <TextEllipsisStaticLine
                          text={topic?.topicTitle || newChatText}
                          isI18n={false}
                          lineClamp={1}
                        />
                      </span>
                    )}
                    <div
                      className="session-actions"
                      onClick={(e) => e.stopPropagation()}
                    >
                      {isEditing ? (
                        <>
                          <Icon
                            className="icon-delete action-btn"
                            style={{ fontSize: 14 }}
                            onClick={(e) => {
                              e.preventDefault()
                              cancelEditing()
                            }}
                          />
                          <Icon
                            className="icon-tick action-btn"
                            onClick={(e) => {
                              e.preventDefault()
                              commitEditing()
                            }}
                          />
                        </>
                      ) : (
                        <>
                          <Icon
                            className="icon-edit-fill action-btn"
                            onClick={(e) => startEditing(e, topic)}
                          />
                          <Icon
                            className="icon-trash-fill action-btn"
                            onClick={() => handleDeleteTopic(topic)}
                          />
                        </>
                      )}
                    </div>
                  </Element>
                )
              })}
            </div>
          </ScrollComponent>
        )}
        <div className="new-chat-container">
          <Element
            className={
              topicActive?.topicId ? 'new-chat-btn' : 'new-chat-btn-disable'
            }
            onClick={handleCreateNewTopic}
          >
            <Icon style={{ fontSize: 14 }} className="icon-plus-circle-fill" />
            <Element style={{ fontSize: 14 }}>
              <Translate value="chatbot.NEW_CHAT" />
            </Element>
          </Element>
        </div>
      </div>
      <PopupDeleteHistoryChatbot
        isShow={isShowPopupDelete}
        setShow={setShowPopupDelete}
        topicSelectDelete={topicSelectDelete}
      />
    </>
  )
}

export default HistoryChatbot
